import React from 'react';
import { AppSettings } from '../types'; 

interface KnowledgeBasePageProps {
  settings: AppSettings; 
}

const BookOpenSolidIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 mr-2 text-[#003794]" {...props}>
    <path d="M11.25 4.533A9.707 9.707 0 006 3C4.207 3 2.45 3.536 1.023 4.428v14.244c1.436-.908 3.203-1.482 5.014-1.482 1.428 0 2.797.338 4.017.939V4.533zM22.94 4.428C21.512 3.536 19.755 3 17.963 3a9.707 9.707 0 00-5.288 1.533v14.923c1.22-.599 2.593-.936 4.016-.936 1.81 0 3.578.573 5.014 1.482V4.428z" />
  </svg>
);


export const KnowledgeBasePage: React.FC<KnowledgeBasePageProps> = ({ settings }) => {
  
  const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <section className="mb-8 p-6 bg-white rounded-lg shadow-lg">
      <h2 className="text-2xl font-semibold text-slate-800 mb-4 border-b border-slate-200 pb-2">{title}</h2>
      <div className="space-y-3 text-slate-700 leading-relaxed">
        {children}
      </div>
    </section>
  );

  return (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow-md">
        <div className="flex items-center mb-1">
            <BookOpenSolidIcon />
            <h1 className="text-3xl font-bold text-[#003794]">Knowledge Base</h1>
        </div>
        <p className="text-md text-slate-600">Welcome to the Tech Medics Command Center Help Guide!</p>
      </div>

      <Section title="Overview & Dashboard">
        <p>The <strong>Dashboard</strong> is your main hub. It provides a quick overview of:</p>
        <ul className="list-disc list-inside ml-4 space-y-1">
          <li><strong>Active Repairs:</strong> Number of tickets not yet completed.</li>
          <li><strong>Revenue:</strong> (Simulated) Revenue for selected periods (Today, Yesterday, This Week, This Month).</li>
          <li><strong>New Repairs Today:</strong> Tickets created since midnight.</li>
          <li><strong>Repair Type Breakdown:</strong> (Sample) A visual representation of common repair types.</li>
          <li><strong>Recent Repairs Table:</strong> A quick view of the latest 5 tickets, allowing direct status updates and payment processing.</li>
        </ul>
      </Section>

      <Section title="Ticket Management">
        <h3 className="text-xl font-medium text-slate-700 mt-2 mb-2">Creating a New Ticket</h3>
        <p>Click the <strong>"New Ticket"</strong> button in the sidebar. A modal will appear where you can fill in:</p>
        <ul className="list-disc list-inside ml-4 space-y-1">
          <li>Customer details (Name, Phone, Email). If the customer is new, a profile will be auto-created.</li>
          <li>Device information (Model, Serial Number, Passcode).</li>
          <li>Issue details (Subject, Description).</li>
          <li>Ticket priority and amount due.</li>
        </ul>
        <h3 className="text-xl font-medium text-slate-700 mt-4 mb-2">Viewing Tickets</h3>
        <p>Use the <strong>"Ticket Filters"</strong> in the sidebar to view tickets by status (New, In Progress, etc.) or select "All Tickets".</p>
        <h3 className="text-xl font-medium text-slate-700 mt-4 mb-2">Ticket Details & Actions</h3>
        <p>Each ticket card displays key information. You can perform several actions:</p>
        <ul className="list-disc list-inside ml-4 space-y-1">
          <li><strong>Show/Hide Summary:</strong> Uses Gemini AI to generate a concise summary of the ticket description.</li>
          <li><strong>Print Label:</strong> Generates a printable device label with Ticket ID, customer info, and a QR code.</li>
          <li><strong>Update Status:</strong> Opens a modal to change the ticket's status (e.g., New {'>'} In Progress {'>'} Completed). If marking as 'Completed', you can opt to simulate an SMS notification to the customer.</li>
          <li><strong>Take Payment:</strong> For 'Ready for Pickup' or 'Completed' tickets with an amount due, this opens the Square payment modal (sandbox mode).</li>
        </ul>
      </Section>
      
      <Section title="Customer Self Check-in Kiosk">
        <p>Navigate to <strong>Kiosk Mode {'>'} Self Check-in Kiosk</strong>. This page is designed for customers to enter their own repair requests.</p>
        <ul className="list-disc list-inside ml-4 space-y-1">
            <li>Customers fill in their contact info, device details, and the issue.</li>
            <li>They must agree to the terms (which includes information about a $40 fee for keeping their original replaced screen).</li>
            <li>Upon submission, a new ticket is created with 'Medium' priority and $0 amount due (can be adjusted later by staff).</li>
            <li>A success screen shows their new Ticket ID.</li>
        </ul>
        <p>The "How did you hear about us?" dropdown helps track referral sources.</p>
      </Section>

      <Section title="Customer Management">
        <p>Navigate to <strong>Customers</strong> in the sidebar.</p>
        <ul className="list-disc list-inside ml-4 space-y-1">
          <li><strong>Search Customers:</strong> Find customers by name, email, or phone.</li>
          <li><strong>View Profile:</strong> Clicking "View Profile" on a customer card (or their name in a ticket) takes you to their detailed profile.</li>
          <li><strong>Profile Page:</strong> Shows customer contact info, their complete repair history (each ticket is interactive), and a section to add and view customer-specific notes.</li>
        </ul>
      </Section>

      <Section title="Inventory Management">
        <p>Navigate to <strong>Inventory</strong> in the sidebar.</p>
        <ul className="list-disc list-inside ml-4 space-y-1">
          <li>View a list of your inventory items with SKU, category, stock levels, price, and cost.</li>
          <li>Stock levels are color-coded (Green for ample, Yellow for low, Red for very low).</li>
          <li><strong>Sync with Square:</strong> (Simulated) Clicking this button mimics fetching updated stock levels. In a real app, this requires backend integration with Square's Inventory API.</li>
          <li><strong>Print Label:</strong> Each inventory item has a button to generate a printable label with name, SKU, price, and a QR code.</li>
        </ul>
      </Section>
      
      <Section title="Labels Page">
        <p>Navigate to <strong>Labels</strong> in the sidebar for centralized label generation.</p>
        <ul className="list-disc list-inside ml-4 space-y-1">
          <li><strong>Device Labels:</strong> Select a ticket from a dropdown to generate its device label.</li>
          <li><strong>Inventory Labels:</strong> Select an inventory item from a dropdown to generate its label.</li>
          <li>Both options use the print modal for preview and printing.</li>
        </ul>
      </Section>

      <Section title="Admin Area">
        <p>The <strong>Admin</strong> section in the sidebar provides access to system-wide configurations.</p>
        <ul className="list-disc list-inside ml-4 space-y-1">
          <li><strong>Settings:</strong> Manage company information, default ticket priority, and user agreement texts. Also displays (read-only) configured Square Sandbox IDs and a reminder about Gemini API Key setup.</li>
          <li><strong>Users:</strong> View a list of employees with their roles, status, and last login. (Add/Edit/Delete functionality is currently a placeholder).</li>
        </ul>
      </Section>

      <Section title="Submitting Suggestions">
        <p>Have an idea to improve the app? Click <strong>"Submit Suggestion"</strong> in the sidebar (under Support Channels). A modal will appear for you to type and send your feedback (currently simulated).</p>
      </Section>
      
      <Section title="Important Notes & Simulated Features">
        <ul className="list-disc list-inside ml-4 space-y-1">
            <li><strong>Gemini API for Summaries:</strong> Ticket summaries require a valid Gemini API Key set as `process.env.API_KEY` in your environment.</li>
            <li><strong>Square Payments:</strong> Uses Square Sandbox. Actual payment processing requires backend integration with your Square Access Token.</li>
            <li><strong>Inventory Sync:</strong> The "Sync with Square" button is a simulation. Real-time inventory sync needs backend logic.</li>
            <li><strong>Data Persistence:</strong> All data (tickets, customers, settings, etc.) is currently stored in-memory and will reset when the application is reloaded. A backend database is needed for persistence.</li>
            <li><strong>SMS Notifications:</strong> Customer SMS for completed tickets is simulated with console logs and alerts. Actual SMS requires integration with a service like Twilio.</li>
        </ul>
      </Section>

      <div className="text-center text-sm text-slate-500 mt-8">
        <p>We hope this guide helps you make the most of the Tech Medics Command Center! ({settings.companyName})</p>
        <p>Last updated: {new Date().toLocaleDateString()}</p>
      </div>
    </div>
  );
};